import React, { useState, useEffect, useCallback } from "react";
import { getManualMeters, createManualMeter, createManualMetersBatch, updateManualMeter, deleteManualMeter } from "../services/api";
import { useAuth } from "../context/AuthContext";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { Search, X, Plus, Edit2, Trash2, ChevronLeft, ChevronRight } from "lucide-react";

const PAGE_SIZE = 50;

const EMPTY_FORM = { meter_code: "", address: "", latitude: "", longitude: "", notes: "" };

export default function MedidoresManualPage() {
  const { user } = useAuth();
  const isAdminPlus = user?.role === "admin-plus";

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [modal, setModal] = useState(null); // null | { mode: "create" | "edit", data: {} }
  const [form, setForm] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);

  const [creationType, setCreationType] = useState("individual");
  const [barrioForm, setBarrioForm] = useState({ neighborhoodName: "", latitude: "", longitude: "", meterCodesList: "" });

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const load = useCallback(async (p = 1) => {
    setLoading(true);
    setError("");
    try {
      const res = await getManualMeters({ search: search || undefined, page: p, limit: PAGE_SIZE });
      setRows(res.data);
      setTotal(res.total);
      setPage(p);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => { load(1); }, [load]);

  function applySearch() { setSearch(searchInput.trim()); }
  function clearSearch() { setSearchInput(""); setSearch(""); }

  function parseMeterEntries(text) {
    return text
      .split(/\n/)
      .map((line) => {
        const [rawCode, rawLote = ""] = line.split(",");
        return { meter_code: rawCode.trim(), lote: rawLote.trim() };
      })
      .filter((e) => e.meter_code);
  }

  function openCreate() {
    setForm(EMPTY_FORM);
    setBarrioForm({ neighborhoodName: "", latitude: "", longitude: "", meterCodesList: "" });
    setCreationType("individual");
    setFormError("");
    setModal({ mode: "create" });
  }

  function openEdit(row) {
    setForm({
      meter_code: row.meter_code || "",
      address: row.address || "",
      latitude: row.latitude ?? "",
      longitude: row.longitude ?? "",
      notes: row.notes || "",
    });
    setFormError("");
    setModal({ mode: "edit", id: row.id });
  }

  function closeModal() { setModal(null); }

  async function handleSave() {
    if (modal.mode === "create" && creationType === "barrio") {
      if (!barrioForm.neighborhoodName.trim()) { setFormError("El nombre del barrio es requerido."); return; }
      const entries = parseMeterEntries(barrioForm.meterCodesList);
      if (entries.length === 0) { setFormError("Ingresá al menos un medidor con el formato: número, lote."); return; }
      setSaving(true);
      setFormError("");
      try {
        const res = await createManualMetersBatch({
          entries,
          address: barrioForm.neighborhoodName.trim(),
          latitude: barrioForm.latitude !== "" ? barrioForm.latitude : null,
          longitude: barrioForm.longitude !== "" ? barrioForm.longitude : null,
        });
        closeModal();
        load(1);
        if (res.routeCreated) {
          setError(`✓ ${res.created} medidores creados. Ruta "${barrioForm.neighborhoodName.trim()}" generada automáticamente en la sección Rutas.`);
          setTimeout(() => setError(""), 6000);
        }
      } catch (err) {
        setFormError(err.message);
      } finally {
        setSaving(false);
      }
      return;
    }

    if (!form.meter_code.trim()) { setFormError("El código del medidor es requerido."); return; }
    setSaving(true);
    setFormError("");
    try {
      const payload = {
        meter_code: form.meter_code.trim(),
        address: form.address.trim() || null,
        latitude: form.latitude !== "" ? form.latitude : null,
        longitude: form.longitude !== "" ? form.longitude : null,
        notes: form.notes.trim() || null,
      };
      if (modal.mode === "create") {
        await createManualMeter(payload);
      } else {
        await updateManualMeter(modal.id, payload);
      }
      closeModal();
      load(modal.mode === "create" ? 1 : page);
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    setDeleting(true);
    try {
      await deleteManualMeter(deleteTarget.id);
      setDeleteTarget(null);
      load(page);
    } catch (err) {
      setError(err.message);
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div>
      <div style={s.topBar}>
        <h1 style={s.title}>Medidores manuales</h1>
        {isAdminPlus && (
          <button style={s.btnPrimary} onClick={openCreate}>
            <Plus size={14} style={{ marginRight: 6 }} />
            Nuevo medidor
          </button>
        )}
      </div>

      {/* Filtros */}
      <div style={s.filterBar}>
        <div style={s.searchRow}>
          <input
            style={s.searchInput}
            placeholder="Buscar por código o dirección..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && applySearch()}
          />
          {search ? (
            <button style={s.iconBtn} onClick={clearSearch}><X size={14} /></button>
          ) : (
            <button style={s.iconBtn} onClick={applySearch}><Search size={14} /></button>
          )}
        </div>
        <div style={s.totalBadgeWrap}>
          <span style={s.totalBadge}>{total.toLocaleString("es-AR")}</span>
          <span style={s.totalLabel}>medidores</span>
        </div>
      </div>

      {/* Tabla */}
      <div style={s.card}>
        {error && (
          <div style={error.startsWith("✓") ? s.successBox : s.errorBox}>{error}</div>
        )}

        {loading ? (
          <div style={s.empty}>Cargando...</div>
        ) : rows.length === 0 ? (
          <div style={s.empty}>
            {search ? "No se encontraron medidores con ese criterio." : "Todavía no hay medidores cargados manualmente."}
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={s.table}>
              <thead>
                <tr>
                  {["Código", "Dirección", "Latitud", "Longitud", "Notas", "Creado por", "Fecha"].map((h) => (
                    <th key={h} style={s.th}>{h}</th>
                  ))}
                  {isAdminPlus && <th style={s.th} />}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id} style={s.tr}>
                    <td style={s.td}><strong>{row.meter_code}</strong></td>
                    <td style={{ ...s.td, maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {row.address || "—"}
                    </td>
                    <td style={s.td}>{row.latitude ?? "—"}</td>
                    <td style={s.td}>{row.longitude ?? "—"}</td>
                    <td style={{ ...s.td, maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {row.notes || "—"}
                    </td>
                    <td style={s.td}>{row.created_by_name || "—"}</td>
                    <td style={{ ...s.td, whiteSpace: "nowrap" }}>
                      {format(new Date(row.created_at), "dd/MM/yy HH:mm", { locale: es })}
                    </td>
                    {isAdminPlus && (
                      <td style={{ ...s.td, whiteSpace: "nowrap" }}>
                        <button style={s.actionBtn} onClick={() => openEdit(row)} title="Editar">
                          <Edit2 size={14} />
                        </button>
                        <button style={{ ...s.actionBtn, color: "#dc2626" }} onClick={() => setDeleteTarget(row)} title="Eliminar">
                          <Trash2 size={14} />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 && (
          <div style={s.pagination}>
            <button style={{ ...s.pageBtn, opacity: page <= 1 ? 0.4 : 1 }} disabled={page <= 1} onClick={() => load(page - 1)}>
              <ChevronLeft size={14} />
            </button>
            <span style={s.pageInfo}>Página {page} de {totalPages}</span>
            <button style={{ ...s.pageBtn, opacity: page >= totalPages ? 0.4 : 1 }} disabled={page >= totalPages} onClick={() => load(page + 1)}>
              <ChevronRight size={14} />
            </button>
          </div>
        )}
      </div>

      {/* Modal crear/editar */}
      {modal && (
        <div style={s.overlay} onClick={closeModal}>
          <div style={s.modal} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <span style={s.modalTitle}>{modal.mode === "create" ? "Nuevo medidor" : "Editar medidor"}</span>
              <button style={s.modalClose} onClick={closeModal}>×</button>
            </div>
            <div style={s.modalBody}>
              {/* Toggle individual / barrio — solo en creación */}
              {modal.mode === "create" && (
                <div style={s.typeToggle}>
                  <button
                    type="button"
                    style={{ ...s.typeBtn, ...(creationType === "individual" ? s.typeBtnActive : {}) }}
                    onClick={() => { setCreationType("individual"); setFormError(""); }}
                  >
                    Individual
                  </button>
                  <button
                    type="button"
                    style={{ ...s.typeBtn, ...(creationType === "barrio" ? s.typeBtnActive : {}) }}
                    onClick={() => { setCreationType("barrio"); setFormError(""); }}
                  >
                    Barrio
                  </button>
                </div>
              )}

              {/* Formulario individual */}
              {(modal.mode === "edit" || creationType === "individual") && (
                <>
                  <Field label="Código de medidor *">
                    <input
                      style={s.input}
                      placeholder="Ej: 12345678"
                      value={form.meter_code}
                      onChange={(e) => setForm((f) => ({ ...f, meter_code: e.target.value }))}
                      autoFocus
                    />
                  </Field>
                  <Field label="Dirección">
                    <input
                      style={s.input}
                      placeholder="Opcional"
                      value={form.address}
                      onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
                    />
                  </Field>
                  <div style={s.rowTwo}>
                    <Field label="Latitud">
                      <input
                        style={s.input}
                        type="number"
                        step="any"
                        placeholder="Opcional"
                        value={form.latitude}
                        onChange={(e) => setForm((f) => ({ ...f, latitude: e.target.value }))}
                      />
                    </Field>
                    <Field label="Longitud">
                      <input
                        style={s.input}
                        type="number"
                        step="any"
                        placeholder="Opcional"
                        value={form.longitude}
                        onChange={(e) => setForm((f) => ({ ...f, longitude: e.target.value }))}
                      />
                    </Field>
                  </div>
                  <Field label="Notas">
                    <textarea
                      style={{ ...s.input, resize: "vertical", minHeight: 64 }}
                      placeholder="Opcional"
                      value={form.notes}
                      onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                    />
                  </Field>
                </>
              )}

              {/* Formulario barrio */}
              {modal.mode === "create" && creationType === "barrio" && (
                <>
                  <Field label="Nombre del barrio *">
                    <input
                      style={s.input}
                      placeholder="Ej: Barrio Los Jardines"
                      value={barrioForm.neighborhoodName}
                      onChange={(e) => setBarrioForm((f) => ({ ...f, neighborhoodName: e.target.value }))}
                      autoFocus
                    />
                  </Field>
                  <div style={s.rowTwo}>
                    <Field label="Latitud">
                      <input
                        style={s.input}
                        type="number"
                        step="any"
                        placeholder="Opcional"
                        value={barrioForm.latitude}
                        onChange={(e) => setBarrioForm((f) => ({ ...f, latitude: e.target.value }))}
                      />
                    </Field>
                    <Field label="Longitud">
                      <input
                        style={s.input}
                        type="number"
                        step="any"
                        placeholder="Opcional"
                        value={barrioForm.longitude}
                        onChange={(e) => setBarrioForm((f) => ({ ...f, longitude: e.target.value }))}
                      />
                    </Field>
                  </div>
                  <Field label="Medidores * (número, lote — uno por línea)">
                    <textarea
                      style={{ ...s.input, resize: "vertical", minHeight: 140, fontFamily: "monospace" }}
                      placeholder={"12345678, A1\n87654321, B3\n11223344, C7"}
                      value={barrioForm.meterCodesList}
                      onChange={(e) => setBarrioForm((f) => ({ ...f, meterCodesList: e.target.value }))}
                    />
                  </Field>
                  {barrioForm.meterCodesList.trim() && (
                    <div style={{ fontSize: 12, color: "var(--text-2)", marginBottom: 4, fontWeight: 600 }}>
                      {parseMeterEntries(barrioForm.meterCodesList).length} medidores ingresados
                    </div>
                  )}
                </>
              )}

              {formError && <div style={s.errorBox}>{formError}</div>}

              <button style={{ ...s.btnPrimary, width: "100%", marginTop: 8 }} onClick={handleSave} disabled={saving}>
                {saving
                  ? "Guardando..."
                  : modal.mode === "create" && creationType === "barrio"
                    ? `Crear ${parseMeterEntries(barrioForm.meterCodesList).length || 0} medidores`
                    : modal.mode === "create"
                      ? "Crear medidor"
                      : "Guardar cambios"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmar eliminación */}
      {deleteTarget && (
        <div style={s.overlay} onClick={() => setDeleteTarget(null)}>
          <div style={{ ...s.modal, maxWidth: 380 }} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <span style={s.modalTitle}>Eliminar medidor</span>
              <button style={s.modalClose} onClick={() => setDeleteTarget(null)}>×</button>
            </div>
            <div style={s.modalBody}>
              <p style={{ fontSize: 14, color: "var(--text)", marginBottom: 20 }}>
                ¿Eliminás el medidor <strong>{deleteTarget.meter_code}</strong>? Esta acción no se puede deshacer.
              </p>
              <div style={{ display: "flex", gap: 10 }}>
                <button style={{ ...s.btnPrimary, flex: 1, background: "#dc2626" }} onClick={handleDelete} disabled={deleting}>
                  {deleting ? "Eliminando..." : "Eliminar"}
                </button>
                <button style={{ ...s.btnSecondary, flex: 1 }} onClick={() => setDeleteTarget(null)}>
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{
        display: "block", fontSize: 11, fontWeight: 700,
        color: "var(--text-2)", textTransform: "uppercase",
        letterSpacing: "0.05em", marginBottom: 6,
      }}>{label}</label>
      {children}
    </div>
  );
}

const s = {
  topBar: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 },
  title: { fontSize: 24, fontWeight: 700, color: "var(--text)", margin: 0 },

  filterBar: { display: "flex", alignItems: "center", gap: 12, marginBottom: 16 },
  searchRow: { display: "flex", gap: 6 },
  searchInput: {
    padding: "9px 12px", borderRadius: 8, border: "1px solid var(--border)",
    fontSize: 13, color: "var(--text)", background: "var(--bg-card)",
    fontFamily: "inherit", outline: "none", width: 280,
  },
  iconBtn: {
    padding: "9px 11px", borderRadius: 8, border: "1px solid var(--border)",
    background: "var(--bg-card)", color: "var(--text-2)", cursor: "pointer",
    display: "flex", alignItems: "center",
  },
  totalBadgeWrap: { display: "flex", alignItems: "center", gap: 6, marginLeft: "auto" },
  totalBadge: { background: "var(--primary-bg)", color: "#D4620D", borderRadius: 8, padding: "4px 12px", fontSize: 15, fontWeight: 700 },
  totalLabel: { fontSize: 13, color: "var(--text-2)", fontWeight: 600 },

  card: { background: "var(--bg-card)", borderRadius: 14, padding: 20, boxShadow: "0 2px 8px rgba(0,0,0,0.06)" },

  errorBox:   { background: "var(--error-bg)",   color: "var(--error-text)",   borderRadius: 9, padding: "10px 14px", fontSize: 13, fontWeight: 600, marginBottom: 14 },
  successBox: { background: "var(--success-bg)", color: "var(--success-text)", borderRadius: 9, padding: "10px 14px", fontSize: 13, fontWeight: 600, marginBottom: 14 },
  empty: { textAlign: "center", padding: "40px 0", color: "var(--text-2)", fontSize: 14 },

  table: { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  th: {
    padding: "11px 14px", textAlign: "left", fontSize: 11, fontWeight: 700,
    color: "var(--text-2)", borderBottom: "1px solid var(--border)",
    textTransform: "uppercase", letterSpacing: "0.05em", whiteSpace: "nowrap",
  },
  tr: { borderBottom: "1px solid var(--border)" },
  td: { padding: "11px 14px", color: "var(--text)", verticalAlign: "middle" },

  actionBtn: {
    background: "none", border: "none", cursor: "pointer",
    color: "var(--text-2)", padding: "4px 6px", borderRadius: 6,
    display: "inline-flex", alignItems: "center",
  },

  pagination: { display: "flex", alignItems: "center", justifyContent: "center", gap: 12, marginTop: 20, paddingTop: 16, borderTop: "1px solid var(--border)" },
  pageBtn: { display: "flex", alignItems: "center", justifyContent: "center", width: 32, height: 32, borderRadius: 8, border: "1px solid var(--border)", background: "var(--bg-card)", color: "var(--text-2)", cursor: "pointer" },
  pageInfo: { fontSize: 13, fontWeight: 600, color: "var(--text-2)" },

  btnPrimary: {
    display: "inline-flex", alignItems: "center", padding: "10px 18px",
    borderRadius: 9, border: "none", background: "#D4620D", color: "#fff",
    fontWeight: 700, cursor: "pointer", fontSize: 13, fontFamily: "inherit",
    transition: "opacity .15s",
  },
  btnSecondary: {
    padding: "10px 18px", borderRadius: 9, border: "1px solid var(--border)",
    background: "var(--bg-card)", color: "var(--text-2)", fontWeight: 700,
    cursor: "pointer", fontSize: 13, fontFamily: "inherit",
  },

  overlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999, padding: 24 },
  modal: { background: "var(--bg-card)", borderRadius: 16, width: "100%", maxWidth: 480, maxHeight: "90vh", overflowY: "auto", boxShadow: "0 16px 48px rgba(0,0,0,0.25)" },
  modalHeader: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "18px 20px 16px", borderBottom: "1px solid var(--border)" },
  modalTitle: { fontSize: 15, fontWeight: 700, color: "var(--text)" },
  modalClose: { background: "none", border: "none", fontSize: 22, color: "var(--text-2)", cursor: "pointer", lineHeight: 1, padding: "0 4px" },
  modalBody: { padding: 20 },

  input: {
    width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px solid var(--border)",
    fontSize: 13, color: "var(--text)", background: "var(--bg-card)",
    fontFamily: "inherit", outline: "none", boxSizing: "border-box",
  },
  rowTwo: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 },

  typeToggle: { display: "flex", borderRadius: 9, overflow: "hidden", border: "1px solid var(--border)", marginBottom: 18 },
  typeBtn: { flex: 1, padding: "10px 0", border: "none", background: "var(--bg-card)", color: "var(--text-2)", fontWeight: 700, cursor: "pointer", fontSize: 13, fontFamily: "inherit", transition: "background .15s, color .15s" },
  typeBtnActive: { background: "#D4620D", color: "#fff" },
};
